import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Testimonials } from '../components/Testimonials';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ClipboardCheck, Target, CalendarCheck, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import { SEO, BreadcrumbSchema } from '../components/SEO';

export default function HowItWorksPage() {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'How It Works', path: '/how-it-works' }
  ];

  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      <SEO 
        title="How It Works - RenoNovo Appointment Generation Process"
        description="Learn how RenoNovo generates exclusive appointments for home service contractors. Our 4-step process: Discovery, Campaign Setup, Lead Qualification, and Close & Scale."
        keywords="how appointment generation works, contractor lead generation process, exclusive territory leads, pay per appointment"
        canonical="/how-it-works"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-24 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
              How It Works
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] tracking-tight leading-tight mb-6">
              From First Call to<br />
              <span className="text-[#2563eb]">Booked Appointments</span>
            </h1>
            <p className="text-lg text-[#64748b] leading-relaxed max-w-3xl mx-auto mb-8">
              Our proven 4-step process ensures you receive only high-quality appointments 
              with homeowners ready to move forward.
            </p>
            <Link to="/book-call">
              <Button 
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-14 rounded-sm text-base"
                data-testid="how-it-works-hero-cta"
              >
                Start Your Free Strategy Call
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="space-y-24">
              {/* Step 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="bg-[#f8fafc] p-12 rounded-sm relative">
                  <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#0f172a] text-white rounded-full flex items-center justify-center text-2xl font-bold">01</div>
                  <div className="w-24 h-24 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mx-auto">
                    <ClipboardCheck className="w-12 h-12 text-[#2563eb]" />
                  </div>
                </div>
                <div className="space-y-6">
                  <p className="text-sm font-semibold text-[#2563eb] uppercase tracking-wide">Step 01</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Discovery Call</h2>
                  <p className="text-lg text-[#64748b] leading-relaxed">
                    We start with a free strategy call to understand your business, target customers, service area, and growth goals.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Define your ideal customer profile</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Identify your target service areas</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Set appointment volume goals</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="lg:order-2 bg-[#f8fafc] p-12 rounded-sm relative">
                  <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#0f172a] text-white rounded-full flex items-center justify-center text-2xl font-bold">02</div>
                  <div className="w-24 h-24 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mx-auto">
                    <Target className="w-12 h-12 text-[#2563eb]" />
                  </div>
                </div>
                <div className="lg:order-1 space-y-6">
                  <p className="text-sm font-semibold text-[#2563eb] uppercase tracking-wide">Step 02</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Campaign Setup</h2>
                  <p className="text-lg text-[#64748b] leading-relaxed">
                    Our marketing team creates customized campaigns targeting homeowners actively searching for your services.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Lock your exclusive territory</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Build targeted ad campaigns</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Set up tracking & reporting</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="bg-[#f8fafc] p-12 rounded-sm relative">
                  <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#0f172a] text-white rounded-full flex items-center justify-center text-2xl font-bold">03</div>
                  <div className="w-24 h-24 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mx-auto">
                    <CalendarCheck className="w-12 h-12 text-[#2563eb]" />
                  </div>
                </div>
                <div className="space-y-6">
                  <p className="text-sm font-semibold text-[#2563eb] uppercase tracking-wide">Step 03</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Lead Qualification</h2>
                  <p className="text-lg text-[#64748b] leading-relaxed">
                    Every lead goes through our qualification process before becoming an appointment on your calendar.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Verify homeowner intent</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Confirm project scope & budget</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Schedule convenient appointment time</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="lg:order-2 bg-[#f8fafc] p-12 rounded-sm relative">
                  <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#0f172a] text-white rounded-full flex items-center justify-center text-2xl font-bold">04</div>
                  <div className="w-24 h-24 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mx-auto">
                    <TrendingUp className="w-12 h-12 text-[#2563eb]" />
                  </div>
                </div>
                <div className="lg:order-1 space-y-6">
                  <p className="text-sm font-semibold text-[#2563eb] uppercase tracking-wide">Step 04</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Close & Scale</h2>
                  <p className="text-lg text-[#64748b] leading-relaxed">
                    You receive qualified appointments ready to discuss their projects. Close deals and grow your business.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Receive appointment notifications</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Track your ROI in real-time</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" />
                      <span className="text-[#0f172a]">Scale up as needed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Works */}
        <section className="py-20 md:py-32 bg-[#0f172a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
                Why This Works
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
                The RenoNovo Advantage
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Our model is designed to eliminate the frustrations of traditional lead generation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 border border-white/10 rounded-sm">
                <h3 className="text-lg font-bold text-white mb-2">Exclusive Territories</h3>
                <p className="text-white/60 text-sm">Your service area is yours alone. No competition for the same leads.</p>
              </div>
              <div className="p-6 border border-white/10 rounded-sm">
                <h3 className="text-lg font-bold text-white mb-2">Pay Per Appointment</h3>
                <p className="text-white/60 text-sm">Only pay for actual booked appointments, not clicks or impressions.</p>
              </div>
              <div className="p-6 border border-white/10 rounded-sm">
                <h3 className="text-lg font-bold text-white mb-2">Quality Guarantee</h3>
                <p className="text-white/60 text-sm">If an appointment doesn't meet our standards, you don't pay.</p>
              </div>
              <div className="p-6 border border-white/10 rounded-sm">
                <h3 className="text-lg font-bold text-white mb-2">No Long-Term Contracts</h3>
                <p className="text-white/60 text-sm">Month-to-month partnerships based on results, not lock-ins.</p>
              </div>
            </div>
          </div>
        </section>

        <Testimonials />
      </main>
      <Footer />
    </div>
  );
}
